import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Copy, Download, Server, MapPin, Clock, Check, ChevronRight, AlertTriangle } from 'lucide-react';
import NotificationBanner from '../components/NotificationBanner';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// A unified row for the single "SBCs" list — either an installed node or a pending token.
type SbcRow =
  | { kind: 'node'; node: any }
  | { kind: 'pending'; token: any };

export default function SbcOnboarding() {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ ttl_days: 7 });
  const [selected, setSelected] = useState<any>(null); // detail dialog: { kind, ... }

  const tenants = useQuery<any[]>({
    queryKey: ['tenants'],
    queryFn: () => fJson('/api/tenants'),
  });

  const tokens = useQuery<any[]>({
    queryKey: ['sbc-install-tokens'],
    queryFn: () => fJson('/api/sbc/onboarding/tokens'),
    refetchInterval: 5000,
  });

  const nodes = useQuery<any[]>({
    queryKey: ['sbc-nodes'],
    queryFn: () => fJson('/api/sbc/nodes'),
    refetchInterval: 10000,
  });

  const issue = useMutation({
    mutationFn: () => {
      const body: any = { ...form };
      // Translate "1h" preset into ttl_days fractional — backend takes a string interval.
      if (form.ttl_preset === '1h') body.ttl_days = '1/24';
      else if (form.ttl_preset === '7d') body.ttl_days = 7;
      else if (form.ttl_preset === '30d') body.ttl_days = 30;
      return fJson('/api/sbc/onboarding/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['sbc-install-tokens'] });
      setOpen(false);
      // Flat shape (matching the row-click) so the detail dialog reads selected.token_secret directly.
      setSelected({ kind: 'pending', ...r });
    },
    onError: (e: any) => toast('error', e.message),
  });

  const revoke = useMutation({
    mutationFn: (uuid: string) => fJson(`/api/sbc/onboarding/tokens/${uuid}/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'admin revoked from UI' }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sbc-install-tokens'] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => fJson(`/api/sbc/nodes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sbc-nodes'] }); setSelected(null); },
    onError: (e: any) => toast('error', e.message),
  });

  const changeDept = useMutation({
    mutationFn: ({ id, domain_uuid }: { id: string; domain_uuid: string }) =>
      fJson(`/api/sbc/nodes/${id}/department`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain_uuid }),
      }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['sbc-nodes'] });
      setSelected((s: any) => (s ? { ...s, bound_domain_uuid: r.bound_domain_uuid } : s));
      toast('success', 'Department changed — the SBC re-scopes on its next heartbeat');
    },
    onError: (e: any) => toast('error', e.message),
  });

  function copy(text: string, what = 'Copied') {
    // navigator.clipboard only exists in a secure context (HTTPS/localhost). On a
    // plain-HTTP LAN page it's undefined, so fall back to the legacy execCommand.
    let ok = false;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text); ok = true;
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    toast(ok ? 'success' : 'error', ok ? what : 'Copy failed — select the text and press Ctrl+C');
  }

  // Resolve a domain_uuid to a friendly tenant name via the tenants query.
  function tenantName(uuid: string | null | undefined) {
    if (!uuid) return '—';
    const t = (tenants.data || []).find((x: any) => x.domain_uuid === uuid);
    return t?.domain_description || t?.domain_name || uuid.slice(0, 8);
  }

  function isOnline(n: any) {
    return !!n.last_seen && (Date.now() - new Date(n.last_seen).getTime() <= 5 * 60 * 1000);
  }

  const serverUrl = window.location.origin;
  const installerHref = `${serverUrl}/api/sbc/installer/windows`;

  // Merge installed nodes + pending (un-redeemed, un-revoked, un-expired) tokens into one list.
  const rows: SbcRow[] = useMemo(() => {
    const nodeRows: SbcRow[] = (nodes.data || []).map((n: any) => ({ kind: 'node', node: n } as SbcRow));
    const pendingRows: SbcRow[] = (tokens.data || [])
      .filter((t: any) => !t.redeemed_at && !t.revoked_at && new Date(t.expires_at).getTime() > Date.now())
      .map((t: any) => ({ kind: 'pending', token: t } as SbcRow));
    return [...nodeRows, ...pendingRows];
  }, [nodes.data, tokens.data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">SBC Onboarding</h1>
        <div className="flex items-center gap-2">
          <a href={installerHref} download
             title="Generic Windows installer — install on any site box, then enter a token. Bundles WireGuard."
             className="btn-ghost inline-flex items-center gap-2">
            <Download size={14} /> Download Windows Installer
          </a>
          <button onClick={() => { setForm({ ttl_days: 7 }); setOpen(true); }} className="btn-primary inline-flex items-center gap-2">
            <Plus size={14} /> Add SBC
          </button>
        </div>
      </div>
      <p className="text-sm text-textSecondary">
        Each SBC is a Stratus session-border node at a customer site, bound to a tenant. Click <b>Add SBC</b> to issue an installer token, run the installer on the site box, and it appears here once it phones home.
      </p>

      {/* Top-of-page admin notifications: SBC offline, cert expiry, reboot failed, PnP quarantine, firmware advisories */}
      <NotificationBanner />

      {/* ── ONE unified SBC list ─────────────────────────────────────────── */}
      <div className="card p-0 overflow-hidden">
        <div className="p-3 font-medium border-b border-border flex items-center gap-2">
          <Server size={14} /> SBCs
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Tenant</th>
              <th>Machine</th>
              <th>Status</th>
              <th>Last seen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              if (row.kind === 'node') {
                const n = row.node;
                const online = isOnline(n);
                return (
                  <tr
                    key={`node-${n.id}`}
                    className="hover:bg-elevated cursor-pointer"
                    onClick={() => setSelected({ kind: 'node', ...n })}
                  >
                    <td>{n.name}</td>
                    <td className="text-sm">{tenantName(n.bound_domain_uuid)}</td>
                    <td className="text-xs">
                      {n.hostname
                        ? <span className="font-mono">{n.hostname}</span>
                        : <span className="text-textSecondary">—</span>}
                      {n.local_ip && <span className="text-textSecondary"> · {n.local_ip}</span>}
                    </td>
                    <td>
                      {online
                        ? <span className="text-success inline-flex items-center gap-1"><Check size={12} /> online</span>
                        : <span className="text-danger inline-flex items-center gap-1"><Clock size={12} /> offline</span>}
                      {online && n.last_diagnostics && n.last_diagnostics.health && n.last_diagnostics.health !== 'ok' && (
                        <span
                          title={(n.last_diagnostics.issues || []).join(' ')}
                          className={`ml-2 inline-flex items-center gap-1 text-xs ${n.last_diagnostics.health === 'critical' ? 'text-danger' : 'text-warning'}`}>
                          <AlertTriangle size={12} /> {n.last_diagnostics.health}
                        </span>
                      )}
                      {n.split_brain && (
                        <span
                          title={`This SBC is installed on more than one machine (${(n.split_brain_ips || []).join(', ')}). They share one identity and fight over the tunnel, so phones at the site can't hold a registration. Stop the Stratus SBC service on all but one machine.`}
                          className="ml-2 inline-flex items-center gap-1 text-xs text-danger">
                          <AlertTriangle size={12} /> duplicate install
                        </span>
                      )}
                    </td>
                    <td className="text-xs">{n.last_seen ? new Date(n.last_seen).toLocaleString() : '—'}</td>
                    <td className="text-textSecondary"><ChevronRight size={16} /></td>
                  </tr>
                );
              }
              const t = row.token;
              return (
                <tr
                  key={`token-${t.token_uuid}`}
                  className="hover:bg-elevated cursor-pointer"
                  onClick={() => setSelected({ kind: 'pending', ...t })}
                >
                  <td>{t.label || <span className="text-textSecondary">(unnamed)</span>}</td>
                  <td className="text-sm">{tenantName(t.domain_uuid)}</td>
                  <td><span className="text-textSecondary inline-flex items-center gap-1"><Clock size={12} /> pending install</span></td>
                  <td className="text-xs text-textSecondary">not installed yet</td>
                  <td className="text-textSecondary"><ChevronRight size={16} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!rows.length && (
          <div className="p-6 text-center text-textSecondary">
            No SBCs yet. Click <b>Add SBC</b> to issue an installer token.
          </div>
        )}
      </div>

      {/* ── Generate-token dialog (Add SBC) ──────────────────────────────── */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Add SBC"
        size="md"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
            <button
              onClick={() => issue.mutate()}
              disabled={!form.domain_uuid || issue.isPending}
              className="btn-primary"
            >
              {issue.isPending ? 'Issuing…' : 'Issue token'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Tenant *</label>
            <select
              className="input"
              value={form.domain_uuid || ''}
              onChange={(e) => setForm({ ...form, domain_uuid: e.target.value })}
            >
              <option value="">Choose…</option>
              {(tenants.data || []).map((t: any) => (
                <option key={t.domain_uuid} value={t.domain_uuid}>
                  {t.domain_description || t.domain_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Name / label</label>
            <input
              className="input"
              placeholder="e.g. Acme Corp HQ"
              value={form.label || ''}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Token TTL</label>
            <div className="flex gap-2">
              {[
                { v: '1h', label: '1 hour (recommended)' },
                { v: '7d', label: '7 days' },
                { v: '30d', label: '30 days' },
              ].map(o => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm({ ...form, ttl_preset: o.v })}
                  className={`btn-ghost flex-1 ${form.ttl_preset === o.v ? 'bg-primary text-white' : ''}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-textSecondary">
            Single-use bearer, tenant-scoped. Anyone with this token can bring up an SBC bound to the selected tenant. Use <b>1 hour</b> when you're installing right now. Revoke if it leaks.
          </p>
        </div>
      </Dialog>

      {/* ── Unified SBC detail dialog ────────────────────────────────────── */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.kind === 'node' ? (selected?.name || 'SBC') : (selected?.label || 'New SBC')}
        size="lg"
        footer={<button onClick={() => setSelected(null)} className="btn-primary">Done</button>}
      >
        {selected?.kind === 'node' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-textSecondary">Status</div>
              <div>
                {isOnline(selected)
                  ? <span className="text-success inline-flex items-center gap-1"><Check size={12} /> online</span>
                  : <span className="text-danger inline-flex items-center gap-1"><Clock size={12} /> offline</span>}
              </div>
              <div className="text-textSecondary">Tenant</div>
              <div>
                <select
                  className="input py-1 text-sm"
                  value={selected.bound_domain_uuid || ''}
                  disabled={changeDept.isPending}
                  onChange={e => e.target.value && e.target.value !== selected.bound_domain_uuid && changeDept.mutate({ id: selected.id, domain_uuid: e.target.value })}
                >
                  <option value="" disabled>— choose department —</option>
                  {(tenants.data || []).map((t: any) => (
                    <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>
                  ))}
                </select>
              </div>
              <div className="text-textSecondary">Local IP</div>
              <div className="font-mono text-xs">{selected.local_ip || '—'}</div>
              <div className="text-textSecondary">Public IP</div>
              <div className="font-mono text-xs">{selected.public_ip || '—'}</div>
              <div className="text-textSecondary">Last seen</div>
              <div>{selected.last_seen ? new Date(selected.last_seen).toLocaleString() : '—'}</div>
              <div className="text-textSecondary">WG address</div>
              <div className="font-mono text-xs">{selected.wg_address || '—'}</div>
            </div>

            {selected.last_diagnostics && (
              <div className="border-t border-border pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium">Health</span>
                  {selected.last_diagnostics.health === 'ok'
                    ? <span className="text-xs text-success inline-flex items-center gap-1"><Check size={12} /> ok</span>
                    : <span className={`text-xs inline-flex items-center gap-1 ${selected.last_diagnostics.health === 'critical' ? 'text-danger' : 'text-warning'}`}><AlertTriangle size={12} /> {selected.last_diagnostics.health}</span>}
                  {selected.last_diagnostics_at && <span className="text-xs text-textSecondary">· {new Date(selected.last_diagnostics_at).toLocaleTimeString()}</span>}
                </div>
                {Array.isArray(selected.last_diagnostics.issues) && selected.last_diagnostics.issues.length > 0 && (
                  <ul className="text-xs text-danger list-disc pl-5 mb-2 space-y-0.5">
                    {selected.last_diagnostics.issues.map((iss: string, i: number) => <li key={i}>{iss}</li>)}
                  </ul>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div className="text-textSecondary">REGISTERs relayed / answered (1m)</div>
                  <div className="font-mono">{selected.last_diagnostics.relay?.register_forwarded_1m ?? '—'} / {selected.last_diagnostics.relay?.register_responses_1m ?? '—'}{selected.last_diagnostics.relay_blackhole ? '  ⚠ no answers' : ''}</div>
                  <div className="text-textSecondary">Registrations held</div>
                  <div className="font-mono">{selected.last_diagnostics.relay?.registrar_size ?? '—'}</div>
                  <div className="text-textSecondary">WG handshake age</div>
                  <div className="font-mono">{selected.last_diagnostics.wg?.handshake_age_s != null ? selected.last_diagnostics.wg.handshake_age_s + 's' : '—'}{selected.last_diagnostics.wg?.on_ws_fallback ? '  (443 fallback)' : ''}</div>
                  <div className="text-textSecondary">WG endpoint</div>
                  <div className="font-mono break-all">{selected.last_diagnostics.wg?.endpoint || '—'}</div>
                  {selected.last_diagnostics.conflicting_tunnels?.length > 0 && <>
                    <div className="text-danger">Conflicting tunnels</div>
                    <div className="font-mono text-danger">{selected.last_diagnostics.conflicting_tunnels.map((c: any) => c.tunnel).join(', ')}</div>
                  </>}
                </div>
                {Array.isArray(selected.last_diagnostics.recent_errors) && selected.last_diagnostics.recent_errors.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-textSecondary mb-1">Recent errors (from the SBC — newest last)</div>
                    <div className="max-h-40 overflow-auto rounded border border-border bg-bg/60 p-2 font-mono text-[11px] leading-snug space-y-0.5">
                      {selected.last_diagnostics.recent_errors.map((e: any, i: number) => (
                        <div key={i} className={e.level === 'error' ? 'text-danger' : 'text-warning'}>
                          <span className="text-textSecondary">{e.ts ? new Date(e.ts).toLocaleTimeString() : ''}</span>{' '}
                          <span className="uppercase">{e.level}</span> {e.msg}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3 border-t border-border pt-3">
              <div>
                <label className="block text-sm text-textSecondary mb-1">Auth token</label>
                <div className="flex gap-2">
                  <input className="input font-mono text-xs" readOnly value={selected.auth_key || ''} />
                  <button onClick={() => copy(selected.auth_key || '', 'Auth token copied')} className="btn-ghost"><Copy size={14} /></button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">Server URL</label>
                <div className="flex gap-2">
                  <input className="input font-mono text-xs" readOnly value={serverUrl} />
                  <button onClick={() => copy(serverUrl, 'Server URL copied')} className="btn-ghost"><Copy size={14} /></button>
                </div>
              </div>
            </div>

            <a href={`${installerHref}?node=${encodeURIComponent(selected.id)}&k=${encodeURIComponent(selected.auth_key || '')}`} download className="btn-primary inline-flex items-center gap-2 w-fit">
              <Download size={14} /> Download Windows Installer
            </a>

            <div className="card p-3 text-xs text-textSecondary inline-flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 shrink-0" />
              <span>This installer is pre-provisioned for <b>this</b> SBC — node identity, auth key and the full WireGuard tunnel are baked in. Just run it on the site box; it dials its encrypted tunnel out and comes online automatically (no Server URL / token to enter). Running it on new hardware moves this SBC's identity to that box.</span>
            </div>

            <button
              onClick={() => { if (confirm('Delete SBC "' + (selected.name || '') + '"? This removes the node from the system. The box can be re-added later with a new installer.')) del.mutate(selected.id); }}
              className="btn-ghost text-danger inline-flex items-center gap-2 w-fit">
              <Trash2 size={14} /> Delete SBC
            </button>
          </div>
        )}

        {selected?.kind === 'pending' && (
          <div className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-textSecondary mb-1">Token secret</label>
                <div className="flex gap-2">
                  <input className="input font-mono text-xs" readOnly value={selected.token_secret || ''} />
                  <button onClick={() => copy(selected.token_secret || '', 'Token copied')} className="btn-ghost"><Copy size={14} /></button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">Server URL</label>
                <div className="flex gap-2">
                  <input className="input font-mono text-xs" readOnly value={serverUrl} />
                  <button onClick={() => copy(serverUrl, 'Server URL copied')} className="btn-ghost"><Copy size={14} /></button>
                </div>
              </div>
            </div>

            <a href={`${installerHref}?token=${encodeURIComponent(selected.token_secret || '')}`} download className="btn-primary inline-flex items-center gap-2 w-fit">
              <Download size={14} /> Download Pre-Provisioned Installer
            </a>

            <div className="card p-3 text-xs text-textSecondary inline-flex items-start gap-2">
              <Server size={14} className="mt-0.5 shrink-0" />
              <span>This download is <b>fully baked</b> — the SBC's identity and WireGuard tunnel are inside the installer. Run it on the site's Windows box; it dials <b>out</b> and comes online on its own. No prompts, nothing to type, nothing inbound to open. (Single-use — generate a new token for another box.)</span>
            </div>

            <div className="border-t border-border pt-3">
              <button
                onClick={() => { revoke.mutate(selected.token_uuid); setSelected(null); }}
                className="btn-ghost text-danger inline-flex items-center gap-2"
              >
                <Trash2 size={14} /> Revoke
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
